/**
 * LAS REGLAS DEL ARCHIVO DE APARIENCIA, DEL LADO DEL PANEL (FONDO-PANEL-T1, paso del logo).
 *
 * POR QUE ESTAN AQUI TAMBIEN. Las manda el API (NexoAPI ReglasDeApariencia, medido el 17-sep
 * en origin/main): tipo fondo o logo, maximo 5 MB, y el mime se decide por la FIRMA del
 * archivo, no por la extension. El panel las repite para no gastar el viaje y para decirle al
 * dueno que pasa ANTES de subir 5 MB por una red de tablet; el API sigue siendo el que manda.
 *
 * LA FIRMA, NO EL NOMBRE. Un .png renombrado a mano no es un PNG: el API lo rechaza leyendo
 * los primeros bytes. Aqui se hace la misma lectura para que el aviso salga en pantalla y no
 * despues del viaje.
 *
 * Sin DOM y sin red: lo prueba scripts/check-logo-archivo.mjs.
 */

export const TIPOS = ['fondo', 'logo'];
export const MAX_BYTES = 5 * 1024 * 1024;

/** El mime segun los primeros bytes, como lo hace el API. null si no es JPEG ni PNG. */
export function mimePorFirma(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (b.length >= 8 && png.every((v, i) => b[i] === v)) return 'image/png';
  return null;
}

/**
 * Por que NO se puede subir, dicho para el dueno; null si sirve.
 * @param {{name?:string, size:number}} archivo lo que da el input file
 * @param {string} tipo 'logo' o 'fondo'
 * @param {Uint8Array|null} primerosBytes los primeros 8 bytes, si ya se leyeron
 */
export function porQueNoSePuedeSubir(archivo, tipo, primerosBytes = null) {
  if (!TIPOS.includes(tipo)) return 'Solo se puede subir el logo o el fondo.';
  if (!archivo || !Number.isFinite(archivo.size)) return 'Elige un archivo.';
  if (archivo.size <= 0) return 'Ese archivo esta vacio.';
  if (archivo.size > MAX_BYTES) {
    const mb = (archivo.size / (1024 * 1024)).toFixed(1).replace('.', ',');
    return `La imagen pesa ${mb} MB y el maximo son 5 MB. Guardala mas liviana e intenta otra vez.`;
  }
  if (primerosBytes && mimePorFirma(primerosBytes) === null) {
    return 'El archivo no es una imagen PNG ni JPEG, aunque se llame asi. Exporta la imagen de nuevo.';
  }
  return null;
}

/** La URL que devolvio el API, en cualquiera de las dos formas. null si no vino. */
export function urlDeLaSubida(res) {
  const d = res?.data?.data ?? res?.data ?? null;
  const url = d?.url ?? d?.Url ?? null;
  return typeof url === 'string' && url.trim() !== '' ? url.trim() : null;
}

/** El id del archivo guardado, para dejarlo anotado al lado de la URL. */
export function idDeLaSubida(res) {
  const d = res?.data?.data ?? res?.data ?? null;
  const id = d?.id ?? d?.Id ?? null;
  return typeof id === 'string' && id.trim() !== '' ? id.trim() : null;
}

// --- EL LOGO CON O SIN TRANSPARENCIA (FONDO-PANEL-T1 punto 7, Josse 17-sep) ----------------
// Un logo con fondo blanco se ve como un recuadro encima del fondo del negocio. Se avisa ANTES de
// subir, leyendo el archivo: en un PNG, el tipo de color del IHDR (4 = gris con alfa, 6 = RGBA) o
// un bloque tRNS antes de los datos de la imagen (IDAT). Un JPEG nunca tiene transparencia.
// Se avisa, no se bloquea: el dueno puede querer su logo con fondo.

/**
 * @param {Uint8Array} bytes el archivo entero o su comienzo (los bloques antes de IDAT bastan)
 * @returns {{ formato: 'png'|'jpeg'|null, transparente: boolean|null }} null = no se pudo saber
 */
export function transparenciaDelLogo(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  const mime = mimePorFirma(b.subarray(0, 8));
  if (mime === 'image/jpeg') return { formato: 'jpeg', transparente: false };
  if (mime !== 'image/png') return { formato: null, transparente: null };
  if (b.length < 26) return { formato: 'png', transparente: null };
  const tipoColor = b[25];
  if (tipoColor === 4 || tipoColor === 6) return { formato: 'png', transparente: true };
  // Tipos 0, 2 y 3: solo si hay un bloque tRNS antes de IDAT.
  let i = 8;
  while (i + 8 <= b.length) {
    const largo = ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0;
    const tipo = String.fromCharCode(b[i + 4], b[i + 5], b[i + 6], b[i + 7]);
    if (tipo === 'tRNS') return { formato: 'png', transparente: true };
    if (tipo === 'IDAT' || tipo === 'IEND') return { formato: 'png', transparente: false };
    i += 12 + largo;
  }
  return { formato: 'png', transparente: null };   // se corto antes de IDAT: no se sabe
}

/** El aviso para el dueno, o null si no hay nada que decir. */
export function avisoDeTransparencia(r) {
  if (!r || r.transparente !== false) return null;
  return r.formato === 'jpeg'
    ? 'Ojo: un JPG no tiene fondo transparente; el logo se va a ver como un recuadro encima del fondo. Si lo tienes en PNG sin fondo, mejor ese.'
    : 'Ojo: este PNG no tiene fondo transparente; el logo se va a ver como un recuadro encima del fondo. Si lo tienes sin fondo, mejor ese.';
}

// --- EL FONDO QUE PROPONE LA IA (FONDO-PANEL-T1 paso 2, 12-oct) -------------------------
// El flujo de n8n devuelve el fondo en base64 y "Usar este fondo" lo guardaba asi en la ficha
// (account_config.fondo_url), aunque desde FONDO-VPS-T1 la ficha guarda SOLO la URL del archivo.
// Antes de guardar, el base64 se vuelve archivo y se sube; si no se puede, NO se guarda base64.

/** Los bytes de una imagen en base64 (data:image/...;base64,... o base64 pelado). null si no se lee. */
export function bytesDeBase64(src) {
  if (typeof src !== 'string' || src.trim() === '') return null;
  const s = src.trim();
  if (/^https?:\/\//i.test(s)) return null;
  const datos = s.startsWith('data:') ? (/^data:[^,]*;base64,(.*)$/s.exec(s) || [])[1] : s;
  if (!datos || !/^[A-Za-z0-9+/=\s]+$/.test(datos)) return null;
  try {
    const bin = atob(datos.replace(/\s/g, ''));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes.length ? bytes : null;
  } catch {
    return null;
  }
}

/**
 * Que se sube como fondo. Los candidatos van en orden de preferencia (el mejorado por la IA y,
 * si ese no sirve, el original). Devuelve el primero que pasa las reglas del API.
 * @returns {{ url: string } | { bytes: Uint8Array, mime: string, cual: number } | { error: string }}
 */
export function fondoParaGuardar(candidatos) {
  let primerError = null;
  for (let i = 0; i < candidatos.length; i++) {
    const src = candidatos[i];
    if (typeof src === 'string' && /^https?:\/\//i.test(src.trim())) return { url: src.trim() };
    const bytes = bytesDeBase64(src);
    if (!bytes) { primerError = primerError || 'La imagen propuesta no se pudo leer. Pide otra propuesta.'; continue; }
    const mime = mimePorFirma(bytes.subarray(0, 8));
    const noSePuede = porQueNoSePuedeSubir({ size: bytes.length }, 'fondo', bytes.subarray(0, 8));
    if (!mime || noSePuede) { primerError = primerError || noSePuede || 'La imagen propuesta no es PNG ni JPEG.'; continue; }
    return { bytes, mime, cual: i };
  }
  return { error: primerError || 'No hay imagen para guardar.' };
}

// --- EL FONDO QUE SUBE EL DUENO (FONDO-SUBIR-IMAGEN-T1, Josse 17-oct) -------------------
// Hasta hoy un fondo hecho afuera (por ejemplo con ChatGPT) solo entraba por link de Dropbox. Se sube igual que el
// logo. El Desktop lo pinta a pantalla completa: si no es 16:9 se recorta o deja bandas. Se AVISA, no se bloquea.

/**
 * Ancho y alto de la imagen leyendo su cabecera: PNG (IHDR, bytes 16-23) o JPEG (el primer marcador SOFn).
 * Con el comienzo del archivo basta (64 KB cubren casi siempre el SOF de un JPEG). null si no se puede saber.
 * @param {Uint8Array} bytes
 * @returns {{ancho:number, alto:number}|null}
 */
export function medidasDeLaImagen(bytes) {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || []);
  const mime = mimePorFirma(b.subarray(0, 8));
  const u32 = (i) => ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0;
  if (mime === 'image/png') {
    if (b.length < 24) return null;
    const ancho = u32(16);
    const alto = u32(20);
    return ancho > 0 && alto > 0 ? { ancho, alto } : null;
  }
  if (mime !== 'image/jpeg') return null;
  let i = 2;
  while (i + 4 <= b.length) {
    if (b[i] !== 0xff) { i++; continue; }
    const marca = b[i + 1];
    if (marca === 0xff) { i++; continue; }
    if (marca === 0xd8 || marca === 0x01 || (marca >= 0xd0 && marca <= 0xd7)) { i += 2; continue; }
    if (marca === 0xd9 || marca === 0xda) return null; // fin o datos de la imagen sin haber visto el tamano
    const largo = (b[i + 2] << 8) | b[i + 3];
    // SOF0..SOF15 salvo DHT (C4), JPG (C8) y DAC (CC)
    if (marca >= 0xc0 && marca <= 0xcf && marca !== 0xc4 && marca !== 0xc8 && marca !== 0xcc) {
      if (i + 9 > b.length) return null;
      const alto = (b[i + 5] << 8) | b[i + 6];
      const ancho = (b[i + 7] << 8) | b[i + 8];
      return ancho > 0 && alto > 0 ? { ancho, alto } : null;
    }
    i += 2 + largo;
  }
  return null;
}

/** El aviso si el fondo no es 16:9 (con un 3 % de margen); null si lo es o si no se pudo medir. */
export function avisoDeProporcion(medidas) {
  if (!medidas || !medidas.ancho || !medidas.alto) return null;
  const r = medidas.ancho / medidas.alto;
  if (Math.abs(r - 16 / 9) / (16 / 9) <= 0.03) return null;
  return `Ojo: la imagen mide ${medidas.ancho} x ${medidas.alto} y no es 16:9; en el Desktop se va a recortar o va a dejar bandas. Lo recomendado es 16:9, por ejemplo 1920 x 1080.`;
}
